/**
 * Web application entry point
 * Main application logic for web browser environment
 */

import BaseApp from '../core/base-app';
import { useAppStore } from '../stores/app-store';
import { useUserStore } from '../stores/user-store';
import { escapeHtml } from '../core/utilities/dom';
import { formatFileSize } from '../core/utilities/formatting';
import WebFileLoader from './file-loader';
import type { LoadedDocument } from './file-loader';
import { DocumentRenderer } from './renderers/document-renderer';
import ToastService from '../components/toast-service';
import { confirmDialog } from '../components/confirm-dialog';
import { replaceQuestion } from '../components/replace-question';
import type { TranslationParams } from '../i18n/ui-manager.types';
import { nameDialog } from '../components/name-dialog';
import { moveDialog } from '../components/move-dialog';
import { nextcloudConnectDialog } from '../components/nextcloud-connect-dialog';
import { nextcloudFolderPicker } from '../components/nextcloud-folder-picker';
import { logger } from '../core/logger';
import { errorMessage } from '../core/forbidden-message';
import SplashScreen from '../core/splash-screen';
import NavRail from '../components/nav-rail';
import { createRouter, type Router } from './shell/router';
import { Shell } from './shell/shell';
import FilesService, {
  FILES_MAX_UPLOAD_BYTES,
  duplicateOf,
} from '../core/files-service';
import type { FileItem } from '../core/files-service';
import { FILE_LOADER } from '../core/app-constants';
import { GatewayErrorCode, isGatewayError } from '../core/gateway/error-codes';
import {
  createComparePanel,
  type ComparePanel,
  type RunningCompare,
} from '../components/compare-panel';
import CompareService from '../core/compare-service';
import IntegrationsService from '../core/integrations-service';
import type { StoredAnalysis } from '../core/integrations-service';
import type { DocumentEntry } from '../core/document-history';
import { fromIso6393 } from '../core/language-constants';
import {
  hideStoredAnalysisBar,
  renderStoredAnalysisBar,
  storedDocumentType,
} from './stored-analysis';
import { isFilesEnabled } from './files-feature';
import { isIntegrationsEnabled } from './integrations-feature';
import { isTodoEnabled } from './todo-feature';
import { isCompareEnabled } from './compare-feature';
import { listProcessedDocuments } from '../core/processed-documents';
import { createModules } from './shell/module-registry';
import {
  saveIntendedRoute,
  consumeIntendedRoute,
  applyIntendedRoute,
} from './shell/intended-route';
import { defaultPath } from './shell/resolve-route';
import Offcanvas from 'bootstrap/js/src/offcanvas.js';
// #158: per-component Bootstrap. Importing a component registers its data API; the markup uses
// data-bs-toggle="collapse" and "dropdown", so both are loaded here even where no code calls them.
import 'bootstrap/js/src/collapse.js';
import 'bootstrap/js/src/dropdown.js';
import '../styles/index.scss';
import 'typeface-roboto';
import 'typeface-roboto-mono';

/** #140: how often the Reader checks a document it added to the collection. */
const ADD_POLL_MS = 2000;
/** #140: give up waiting after 5 minutes (150 × 2 s); Collection shows it later. */
const ADD_MAX_POLLS = 150;

/** #140: a document's todos in the Todo module. */
const todoPath = (documentId: string): string =>
  `/todo?document=${encodeURIComponent(documentId)}`;

// DOM elements interface
interface WebAppElements extends Record<string, HTMLElement | undefined> {
  fileInput?: HTMLInputElement;
  dropArea?: HTMLElement;
  loading?: HTMLElement;
  documentContent?: HTMLElement;
  documentInfo?: HTMLElement;
  uiLanguageButton?: HTMLElement;
  loadButton?: HTMLButtonElement;
  statusBar?: HTMLElement;
  fileInputArea?: HTMLElement;
  documentStats?: HTMLElement;
  documentLanguage?: HTMLElement;
  accountSettings?: HTMLElement;
  discoveryContainer?: HTMLElement;
  landingPage?: HTMLElement;
  landingOpenBtn?: HTMLButtonElement;
  topbarFilename?: HTMLElement;
  topbarBadge?: HTMLElement;
}

class WebApp extends BaseApp {
  protected override fileLoader!: WebFileLoader;
  protected override elements!: WebAppElements;
  private documentRenderer!: DocumentRenderer;
  private router?: Router;
  private shell?: Shell;
  private filesService: FilesService | null = null;
  private compareService: CompareService | null = null;
  private integrationsService: IntegrationsService | null = null;
  private navRail: NavRail | null = null;
  /** Identity of the last document passed to displayDocument (#92 review, important 1). */
  private lastDisplayedDocument: LoadedDocument | object | null = null;
  /** #140: VITE_FEATURE_COMPARE. */
  private readonly compareEnabled = isCompareEnabled();
  /** #140: the collection document open in the Reader (what Compare compares). */
  private collectionDocument: { id: string; name: string } | null = null;
  private comparePanel: ComparePanel | null = null;
  /** The name in the top bar; null = "No document open" (#157). */
  private topbarDocumentName: string | null = null;
  /** #140: the compare job still running, resumed when the panel mounts again. */
  private runningCompare: RunningCompare | null = null;
  private collectionPicker: HTMLInputElement | null = null;
  /** #140: bumped per Add document to collection and on leaving the Reader. */
  private addRun = 0;

  constructor() {
    super();
    // Note: fileLoader will be initialized in initializeEnvironmentSpecific
    // after gatewayService is available
  }

  /**
   * Initialize the web application
   */
  override async initialize(): Promise<void> {
    logger.info('Initializing IMSight Web App');

    // Call parent initialize method
    await super.initialize();

    // #98 part 4: a recent stored analysis is fetched again, not read from a file.
    if (this.documentManager) {
      this.documentManager.reopenSource = (entry) =>
        this.reopenStoredAnalysis(entry);
    }

    // Refresh the contradiction button when CHECK_RULES results change
    useAppStore.subscribe((state, prev) => {
      if (state.lastCheckResponse !== prev.lastCheckResponse) {
        this.updateProcessButtonState();
      }
    });

    logger.info('Web app initialized successfully');
  }

  /**
   * Initialize environment-specific components (file loader)
   */
  override async initializeEnvironmentSpecific(): Promise<void> {
    // Create document renderer
    this.documentRenderer = new DocumentRenderer({
      uiManager: this.uiManager,
    });

    // Create file loader with gateway service
    this.fileLoader = new WebFileLoader({
      gatewayService: this.gatewayService,
    });

    // Initialize file loader
    this.fileLoader.initialize(this.elements);
  }

  /**
   * #93: the Files service, created once and only in proxy mode (needs
   * authService and gatewayService); null otherwise.
   */
  getFilesService(): FilesService | null {
    if (this.filesService) return this.filesService;
    if (!this.authService || !this.gatewayService) return null;
    this.filesService = new FilesService({
      proxyUrl: `${import.meta.env.VITE_TYPO3_BASE_URL}/index.php?type=3002`,
      authService: this.authService,
    });
    return this.filesService;
  }

  /**
   * #140: the Compare service (type=3002), created once and only in proxy
   * mode (needs authService and gatewayService); null otherwise.
   */
  getCompareService(): CompareService | null {
    if (this.compareService) return this.compareService;
    if (!this.authService || !this.gatewayService) return null;
    this.compareService = new CompareService({
      proxyUrl: `${import.meta.env.VITE_TYPO3_BASE_URL}/index.php?type=3002`,
      authService: this.authService,
    });
    return this.compareService;
  }

  /**
   * #98: the integrations service (type=3006), created once and only in proxy
   * mode (needs authService); null otherwise.
   */
  getIntegrationsService(): IntegrationsService | null {
    if (this.integrationsService) return this.integrationsService;
    if (!this.authService) return null;
    this.integrationsService = new IntegrationsService({
      endpoint: `${import.meta.env.VITE_TYPO3_BASE_URL}/index.php?type=3006`,
      authService: this.authService,
    });
    return this.integrationsService;
  }

  /**
   * #93: open a file from the Files module in the Reader. The loader reports
   * refusals (too large, unsupported) through onErrorOccurred and resolves, so
   * we navigate anyway and the Reader shows its own error toast; a throw from
   * the loader propagates to the Files module's message line.
   */
  async openInReader(
    file: File,
    source?: { id: string; name: string }
  ): Promise<void> {
    if (this.compareEnabled && file.size > FILE_LOADER.MAX_FILE_SIZE_BYTES) {
      // #140: the collection takes up to 50 MB (compare runs server-side), the
      // in-browser Reader does not open those yet.
      throw new Error(this.uiManager.t('compare.readerTooLarge'));
    }
    const before = this.compareEnabled ? this.currentDocument : null;
    await this.fileLoader.loadFile(file);
    // #140: only a document that actually loaded becomes Compare's target; a
    // refused one leaves the previous document (still shown) in place.
    if (this.compareEnabled) {
      const loaded = this.currentDocument;
      if (loaded && loaded !== before) {
        this.collectionDocument = source ?? null;
        void this.comparePanel?.refresh();
      }
    }
    this.shell?.navigate('/reader');
  }

  /** #140: the Reader was left; an Add document to collection in flight stops. */
  private leaveReader(): void {
    this.addRun++;
  }

  private onReaderRoute(): boolean {
    const path = this.router?.current();
    return path === undefined || path === '/reader';
  }

  /**
   * #140, flag on: the Reader's "Add document to collection" — same checks as
   * the Collection upload, upload with prepare, poll until the document is
   * ready, then open it like Collection's Open (files.download + openInReader).
   */
  async addDocumentToCollection(file: File): Promise<void> {
    const t = (key: string, params?: TranslationParams) =>
      this.uiManager.t(key, params);
    const files = this.getFilesService();
    if (!files) return;
    if (file.size > FILES_MAX_UPLOAD_BYTES) {
      this.toastService.showError(
        `${file.name}: ${t('modules.files.tooLarge')}`
      );
      return;
    }
    if (!FILE_LOADER.EXTENSION_PATTERN.test(file.name)) {
      this.toastService.showError(
        `${file.name}: ${t('modules.files.unsupported')}`
      );
      return;
    }
    if (file.size > FILE_LOADER.MAX_FILE_SIZE_BYTES) {
      this.toastService.showError(
        `${file.name}: ${t('compare.readerTooLarge')}`
      );
      return;
    }
    const run = ++this.addRun;
    const cancelled = () => run !== this.addRun || !this.onReaderRoute();
    this.toastService.showInfo(t('compare.add.preparing', { name: file.name }));
    try {
      let item: FileItem | undefined;
      try {
        item = await files.upload(file, 'eng', undefined, null, true);
      } catch (error) {
        // #151: the same Replace / Keep existing question as the Files module.
        const existing = duplicateOf(error);
        if (!existing?.canDelete) throw error;
        const replace = await confirmDialog(replaceQuestion(t, file.name));
        if (cancelled()) return;
        if (!replace) {
          if (existing.status !== 'ready') {
            this.toastService.showInfo(
              t('compare.add.stillPreparing', { name: existing.name })
            );
            return;
          }
          const downloaded = await files.download(existing.id);
          if (cancelled()) return;
          await this.openInReader(downloaded, {
            id: existing.id,
            name: existing.name,
          });
          return;
        }
        item = await files.upload(file, 'eng', existing.id, null, true);
      }
      let attempts = 0;
      while (item && item.status !== 'ready' && item.status !== 'failed') {
        if (attempts >= ADD_MAX_POLLS) {
          this.toastService.showInfo(
            t('compare.add.stillPreparing', { name: item.name })
          );
          return;
        }
        attempts++;
        await new Promise((resolve) => setTimeout(resolve, ADD_POLL_MS));
        if (cancelled()) return;
        const id: string = item.id;
        const listing = await files.listCollection({
          q: item.name,
          pageSize: 100,
        });
        if (cancelled()) return;
        item = listing.items.find((candidate) => candidate.id === id);
      }
      if (!item || item.status !== 'ready') {
        this.toastService.showError(
          t('compare.add.failed', { name: file.name })
        );
        return;
      }
      if (cancelled()) return;
      const downloaded = await files.download(item.id);
      if (cancelled()) return;
      await this.openInReader(downloaded, { id: item.id, name: item.name });
    } catch (error) {
      if (cancelled()) return;
      this.toastService.showError(
        isGatewayError(error, GatewayErrorCode.DUPLICATE_NAME)
          ? t('compare.add.duplicate', { name: file.name })
          : errorMessage(
              error,
              (key) => t(key),
              t('compare.add.failed', { name: file.name })
            )
      );
    }
  }

  /**
   * #140, flag on: the Compare panel for the Reader sidebar (proxy mode only).
   * Returns the cleanup the Reader runs on leave, which stops its polling.
   */
  mountComparePanel(container: HTMLElement): () => void {
    const compare = this.getCompareService();
    const files = this.getFilesService();
    if (!compare || !files) return () => {};
    const panel = createComparePanel({
      t: (key, params) => this.uiManager.t(key, params),
      compare,
      files,
      getDocument: () => this.collectionDocument,
      onDone: (documentId) => this.shell?.navigate(todoPath(documentId)),
      getRunningJob: () => this.runningCompare,
      setRunningJob: (job) => {
        this.runningCompare = job;
      },
      onFinishedElsewhere: (documentId) =>
        this.toastService.showSuccess(
          this.uiManager.t('compare.panel.finished'),
          {
            duration: 10000,
            action: {
              label: this.uiManager.t('compare.panel.openTodos'),
              onClick: () => this.shell?.navigate(todoPath(documentId)),
            },
          }
        ),
    });
    container.replaceChildren(panel.element);
    this.comparePanel = panel;
    void panel.refresh();
    return () => {
      panel.destroy();
      container.replaceChildren();
      if (this.comparePanel === panel) this.comparePanel = null;
    };
  }

  /**
   * #98 part 4: show a stored analysis in the Reader exactly like a live one —
   * the document from the stored Docling result (same path as a parsed upload),
   * then the stored CheckRulesResponse (same path as the end of a live check).
   * No gateway call; Process keeps working as a live check of the loaded text.
   */
  async openStoredAnalysis(
    stored: StoredAnalysis,
    source: { name: string; folderId: string; path: string }
  ): Promise<void> {
    const language = fromIso6393(stored.analysis.language);
    const analyzedAt = stored.analysis.analyzedAt
      ? Date.parse(stored.analysis.analyzedAt)
      : NaN;
    const document = await this.fileLoader.loadParsedDocument({
      name: source.name,
      type: storedDocumentType(source.name),
      text: stored.document.text,
      parse: stored.document.parse,
      ...(language ? { language } : {}),
      ...(Number.isNaN(analyzedAt) ? {} : { lastModified: analyzedAt }),
    });
    document.source = {
      kind: 'nextcloud',
      folderId: source.folderId,
      path: source.path,
    };
    this.handleDocumentLoaded(document);
    this.applyCheckResponse(stored.check);
    this.showStoredAnalysisBar(stored);
    this.shell?.navigate('/reader');
  }

  /** #98 part 4: a recent document that is a stored analysis, opened again from the proxy. */
  async reopenStoredAnalysis(entry: DocumentEntry): Promise<void> {
    const source = entry.source;
    const service = this.getIntegrationsService();
    try {
      if (!source || !service) throw new Error('No stored analysis source');
      const stored = await service.analysis(source.folderId, source.path);
      await this.openStoredAnalysis(stored, {
        name: entry.name,
        folderId: source.folderId,
        path: source.path,
      });
    } catch (error) {
      logger.warn('Stored analysis could not be reopened', error);
      this.toastService.showError(
        this.uiManager.t('modules.reader.storedAnalysis.reopenFailed')
      );
    }
  }

  private showStoredAnalysisBar(stored: StoredAnalysis): void {
    const bar = document.querySelector<HTMLElement>(
      '[data-stored-analysis-bar]'
    );
    if (!bar) return;
    renderStoredAnalysisBar(bar, stored, (key, params) =>
      this.uiManager.t(key, params)
    );
  }

  /** A live check replaces what the stored-analysis bar describes. */
  override async checkDocumentRules(): Promise<void> {
    hideStoredAnalysisBar(
      document.querySelector<HTMLElement>('[data-stored-analysis-bar]')
    );
    return super.checkDocumentRules();
  }

  /** Park the deep link before leaving for the login page (#92). */
  protected override beforeAuthRedirect(): void {
    saveIntendedRoute(window.location.hash);
  }

  /**
   * Log out (#92 follow-up chrome cleanup): only meaningful in proxy mode,
   * where authService is set. Direct/dev mode has nothing to redirect to,
   * so both entry points (Account page, nav rail) are hidden there instead
   * of calling this. Clears person data and logs out in the UI language
   * through the shared BaseApp routine (#108).
   */
  logout(): void {
    if (this.authService) this.logOut(this.authService);
  }

  /**
   * Build the shell and premount the reader before BaseApp looks up its
   * elements (#92, "Boot integration").
   */
  protected override async beforeSetupElements(): Promise<void> {
    const outlet = document.getElementById('module-outlet');
    const topbarSlot = document.getElementById('topbar-slot');
    if (!outlet || !topbarSlot) {
      throw new Error('Shell chrome not found: #module-outlet / #topbar-slot');
    }

    this.router = createRouter();
    this.shell = new Shell({
      outlet,
      router: this.router,
      modules: createModules({
        t: (key, params) => this.uiManager.t(key, params),
        onLanguageChange: (callback) =>
          this.uiManager.onLanguageChange(callback),
        getUserName: () => useUserStore.getState().getUserDisplayName(),
        getLastOpenedName: () =>
          this.documentHistory.getHistory()[0]?.name ?? null,
        topbarSlot,
        closeReaderOverlays: () => this.closeReaderOverlays(),
        getAccountView: () => this.accountSettings,
        navigate: (path) => this.shell?.navigate(path),
        // #92 follow-up chrome cleanup: only offered in proxy mode.
        logout: this.authService ? () => this.logout() : null,
        filesEnabled: isFilesEnabled(),
        integrationsEnabled: isIntegrationsEnabled(),
        todoEnabled: isTodoEnabled(),
        compareEnabled: this.compareEnabled,
        mountComparePanel: this.compareEnabled
          ? (container) => this.mountComparePanel(container)
          : undefined,
        getReaderDocumentId: this.compareEnabled
          ? () => this.collectionDocument?.id ?? null
          : undefined,
        onReaderLeave: this.compareEnabled
          ? () => this.leaveReader()
          : undefined,
        listProcessed: () => listProcessedDocuments(),
        // #96: reopened exactly like a Recent entry (same id, same source).
        openProcessed: (processed) =>
          void this.handleRecentDocumentSelect({
            ...processed,
            openedAt: processed.processedAt,
            displayName: processed.name,
          }),
        getFilesService: () => this.getFilesService(),
        openInReader: (file, source) => this.openInReader(file, source),
        confirm: confirmDialog,
        nameDialog,
        moveDialog,
        getIntegrationsService: () => this.getIntegrationsService(),
        getCompareService: () => this.getCompareService(),
        openStoredAnalysis: (stored, source) =>
          this.openStoredAnalysis(stored, source),
        nextcloudConnectDialog: ({ service }) =>
          nextcloudConnectDialog({
            service,
            t: (key, params) => this.uiManager.t(key, params),
            organizationName: this.authService?.getOrganizationName() ?? '',
          }),
        nextcloudFolderPicker: ({ service }) =>
          nextcloudFolderPicker({
            service,
            t: (key, params) => this.uiManager.t(key, params),
          }),
      }),
      t: (key) => this.uiManager.t(key),
      afterMount: () => this.uiManager.updateUI(),
    });

    await this.shell.premount('reader');
    await this.shell.premount('account');
  }

  /**
   * Render the rail and show the first route; the splash hides after this.
   */
  protected override async beforeReveal(): Promise<void> {
    if (!this.shell || !this.router) return;

    const railContainer = document.getElementById('nav-rail');
    if (railContainer) {
      this.navRail = new NavRail({
        modules: this.shell.getModules(),
        router: this.router,
        uiManager: this.uiManager,
        // #92 follow-up chrome cleanup: Log out on the rail, proxy mode only.
        actions: this.authService
          ? [
              {
                id: 'logout',
                labelKey: 'userMenu.logout',
                icon: 'logout',
                onSelect: () => this.logout(),
              },
            ]
          : undefined,
      });
      this.navRail.initialize(railContainer);
      this.uiManager.onLanguageChange(() => this.navRail?.updateUILanguage());
    }

    applyIntendedRoute(this.router, consumeIntendedRoute());
    await this.shell.start();
  }

  /** The contradiction offcanvas keeps a backdrop on <body>; close it on leave. */
  private closeReaderOverlays(): void {
    const offcanvas = document.getElementById(
      'contradiction-offcanvas-element'
    );
    if (offcanvas) Offcanvas.getInstance(offcanvas)?.hide();
  }

  /**
   * User menu → Account module (#92). Navigate straight to its default
   * subroute so it doesn't push a redirect entry that resets an already
   * active tab (#92 review, minor 1).
   */
  openAccountSettings(): void {
    const accountModule = this.shell
      ?.getModules()
      .find((module) => module.id === 'account');
    this.shell?.navigate(
      accountModule ? defaultPath(accountModule) : '/account'
    );
  }

  /**
   * Set up DOM element references
   */
  override setupElements(): void {
    this.elements = {
      fileInput: document.getElementById('file-input') as HTMLInputElement,
      dropArea: document.getElementById('file-input-area') as HTMLElement,
      loading: document.getElementById('loading') as HTMLElement,
      documentContent: document.getElementById(
        'document-content'
      ) as HTMLElement,
      documentInfo: document.getElementById('document-info') as HTMLElement,
      uiLanguageButton: document.getElementById(
        'ui-language-button'
      ) as HTMLElement,
      loadButton: document.getElementById('load-document') as HTMLButtonElement,
      statusBar: document.getElementById('status-bar') as HTMLElement,
      documentStats: document.getElementById(
        'document-stats-container'
      ) as HTMLElement,
      documentLanguage: document.getElementById(
        'document-language-container'
      ) as HTMLElement,
      accountSettings: document.getElementById(
        'account-settings'
      ) as HTMLElement,
      discoveryContainer: document.getElementById(
        'discovery-container'
      ) as HTMLElement,
      parsingOptionsContainer: document.getElementById(
        'parsing-options-container'
      ) as HTMLElement,
      contradictionOffcanvas: document.getElementById(
        'contradiction-offcanvas'
      ) as HTMLElement,
      landingPage: document.getElementById('landing-page') as HTMLElement,
      landingOpenBtn: document.getElementById(
        'landing-open-btn'
      ) as HTMLButtonElement,
      topbarFilename: document.getElementById('topbar-filename') as HTMLElement,
      topbarBadge: document.getElementById('topbar-badge') as HTMLElement,
    };

    // Validate required elements
    const requiredElements = ['dropArea', 'documentContent', 'loadButton'];
    for (const elementName of requiredElements) {
      if (!this.elements[elementName as keyof WebAppElements]) {
        throw new Error(`Required element not found: ${elementName}`);
      }
    }
  }

  /**
   * Set up event handlers
   */
  override setupEventHandlers(): void {
    // File loader callbacks
    this.fileLoader.onDocumentLoad(this.handleDocumentLoaded.bind(this));
    this.fileLoader.onErrorOccurred(this.handleError.bind(this));
    this.fileLoader.onProgressUpdate(this.handleProgress.bind(this));
    this.fileLoader.onLoadingStart(this.handleLoadingStart.bind(this));
    // The filename has no data-i18n (a re-translation would replace the open
    // document's name, #157): the empty state is re-rendered here instead.
    this.uiManager.onLanguageChange(() => this.renderTopbarFilename());

    if (this.compareEnabled) {
      this.setupAddToCollection();
      // The panel is rebuilt per Reader visit; one hook for whichever is mounted.
      this.uiManager.onLanguageChange(() => this.comparePanel?.retranslate());
    } else {
      // Load document button
      if (this.elements.loadButton) {
        this.elements.loadButton.addEventListener('click', () =>
          this.fileLoader.openFilePicker()
        );
      }

      if (this.elements.landingOpenBtn) {
        this.elements.landingOpenBtn.addEventListener('click', () =>
          this.fileLoader.openFilePicker()
        );
      }
    }

    // In-document fragment links (e.g. Docling TOC anchors like href="#_Toc12")
    // must scroll within the rendered document, not be caught by the
    // shell's strict router and redirected to the dashboard (#92 review,
    // important 2). Delegated on the container so re-renders keep working.
    this.elements.documentContent?.addEventListener(
      'click',
      this.handleDocumentContentClick.bind(this)
    );
  }

  /**
   * #140, flag on: no viewing without adding. Both open buttons and the drop
   * zone add the document to the collection; the footer's Recent list is
   * hidden (reopening goes through Collection).
   */
  private setupAddToCollection(): void {
    const picker = document.createElement('input');
    picker.type = 'file';
    picker.hidden = true;
    picker.dataset.collectionPicker = '';
    const accept = this.elements.fileInput?.getAttribute('accept');
    if (accept) picker.setAttribute('accept', accept);
    picker.addEventListener('change', () => {
      const file = picker.files?.[0];
      picker.value = '';
      if (file) void this.addDocumentToCollection(file);
    });
    document.body.append(picker);
    this.collectionPicker = picker;

    const pick = () => this.collectionPicker?.click();
    this.elements.loadButton?.addEventListener('click', pick);
    this.elements.landingOpenBtn?.addEventListener('click', pick);

    // Capture on the landing page so the loader's own drop/click handlers on
    // the drop zone (which open without adding) never see these events.
    const landing = this.elements.landingPage;
    const dropArea = this.elements.dropArea;
    if (landing && dropArea) {
      landing.addEventListener(
        'drop',
        (event) => {
          if (!dropArea.contains(event.target as Node)) return;
          event.preventDefault();
          event.stopPropagation();
          dropArea.classList.remove('dragover');
          const file = (event as DragEvent).dataTransfer?.files?.[0];
          if (file) void this.addDocumentToCollection(file);
        },
        true
      );
      landing.addEventListener(
        'click',
        (event) => {
          const target = event.target as Node;
          if (target === this.elements.fileInput) return;
          if (!dropArea.contains(target)) return;
          event.stopPropagation();
          pick();
        },
        true
      );
    }

    const recent = document.querySelector<HTMLElement>(
      '.status-bar__section--recent'
    );
    if (recent) recent.hidden = true;
  }

  /** See setupEventHandlers() for why this exists (#92 review, important 2). */
  private handleDocumentContentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    const anchor = target?.closest('a[href^="#"]') as HTMLAnchorElement | null;
    if (!anchor) return;

    const href = anchor.getAttribute('href') ?? '';
    if (href.startsWith('#/')) return; // a real shell route; let it navigate

    // Never let this fall through to the browser's default hash navigation:
    // the router treats any non-"#/…" hash as malformed and redirects home.
    event.preventDefault();

    const id = href.slice(1);
    const container = this.elements.documentContent;
    const fragmentTarget =
      id && container ? this.findFragmentTarget(container, id) : null;
    fragmentTarget?.scrollIntoView();
  }

  /** Finds `id="…"` or `name="…"` inside the document content only. */
  private findFragmentTarget(
    container: HTMLElement,
    id: string
  ): Element | null {
    for (const el of Array.from(container.querySelectorAll('[id]'))) {
      if (el.id === id) return el;
    }
    for (const el of Array.from(container.querySelectorAll('[name]'))) {
      if (el.getAttribute('name') === id) return el;
    }
    return null;
  }

  /**
   * Handle loading start
   */
  handleLoadingStart(): void {
    logger.info('Document loading started');
    this.setLoading(true);
  }

  /**
   * Handle successful document loading
   * @param {any} document - Loaded document
   * @param {FileSystemFileHandle} fileHandle - Optional file handle for File System Access API
   */
  override handleDocumentLoaded(
    document: any,
    fileHandle: FileSystemFileHandle | null = null
  ): void {
    // Stop loading state
    this.setLoading(false);
    // #98 part 4: another document; openStoredAnalysis shows the bar again after this.
    hideStoredAnalysisBar(
      window.document.querySelector<HTMLElement>('[data-stored-analysis-bar]')
    );

    // Call parent implementation which handles all the document loading logic
    // Pass the file handle so it can be stored
    super.handleDocumentLoaded(document, fileHandle);
  }

  /**
   * Handle loading errors
   * @param {Error} error - Error object
   */
  handleError(error: Error): void {
    logger.error('Document loading error:', error);
    this.showError(
      errorMessage(error, (k) => this.uiManager.t(k), error.message)
    );
  }

  /**
   * Handle progress updates
   * @param {number} progress - Progress percentage
   */
  handleProgress(progress: number): void {
    logger.debug(`Loading progress: ${progress}%`);
    // Could update a progress bar here
  }

  /**
   * Display document content with formatting or word-level markup
   * @param {LoadedDocument | any} docData - Document to display
   */
  override displayDocument(docData: LoadedDocument | any): void {
    const contentElement = this.elements.documentContent;

    if (!contentElement) {
      logger.error('Document content element not found');
      return;
    }

    // Initialize document renderer if not already done (for test environments)
    if (!this.documentRenderer) {
      this.documentRenderer = new DocumentRenderer({
        uiManager: this.uiManager,
      });
    }

    // Use document renderer to handle all rendering
    this.documentRenderer.render(docData, contentElement);

    // Show the document content
    contentElement.style.display = 'block';
    this.elements.dropArea!.style.display = 'none';
    if (this.elements.landingPage) {
      this.elements.landingPage.style.display = 'none';
    }
    if (docData.name) {
      this.topbarDocumentName = docData.name;
      this.renderTopbarFilename();
    }
    const sidebarHint = document.getElementById('sidebar-hint');
    if (sidebarHint) sidebarHint.style.display = 'none';

    // A document loaded from anywhere (e.g. the status-bar recent list on
    // the dashboard) is shown where it can be seen (#92). A reprocess (e.g.
    // a language change from the status bar, reachable from every module)
    // passes the same object back in and must not yank the user to the
    // reader; every load — even of the same file — builds a new object.
    if (docData !== this.lastDisplayedDocument) {
      this.lastDisplayedDocument = docData;
      this.shell?.navigate('/reader');
    }
  }

  /**
   * Build document info HTML (web-specific with file size)
   */
  buildDocumentInfoHTML(
    docData: any,
    languageName: string,
    autoText: string
  ): string {
    const metadata = docData.parsed.metadata;
    return `
      <strong>${this.uiManager.t('documentInfo.file')}:</strong> ${escapeHtml(docData.name)}<br>
      <strong>${this.uiManager.t('documentInfo.size')}:</strong> ${formatFileSize(docData.size)}<br>
      <strong>${this.uiManager.t('documentInfo.language')}:</strong> ${escapeHtml(languageName)} ${escapeHtml(autoText)}<br>
      <strong>${this.uiManager.t('documentInfo.words')}:</strong> ${metadata.wordCount}<br>
      <strong>${this.uiManager.t('documentInfo.sentences')}:</strong> ${metadata.sentenceCount}<br>
      <strong>${this.uiManager.t('documentInfo.paragraphs')}:</strong> ${metadata.paragraphCount}<br>
      <strong>${this.uiManager.t('documentInfo.characters')}:</strong> ${metadata.characterCount}<br>
      <strong>${this.uiManager.t('documentInfo.parseTime')}:</strong> ${metadata.parseTime.toFixed(1)}${this.uiManager.t('units.ms')}
    `;
  }

  /**
   * The Analyse buttons live in markup (index.html) and start disabled.
   * This override just updates their enabled state when the document or
   * the latest rule-check response changes.
   */
  override updateEnhancementMenu(): void {
    this.updateProcessButtonState();
  }

  private updateProcessButtonState(): void {
    const processBtn = document.getElementById(
      'check-rules-btn'
    ) as HTMLButtonElement | null;
    const contradictionsBtn = document.getElementById(
      'show-contradictions-btn'
    ) as HTMLButtonElement | null;
    const contradictionsCount = document.getElementById(
      'show-contradictions-count'
    ) as HTMLSpanElement | null;

    const hasDocument = this.currentDocument !== null;
    const ruleCount =
      useAppStore.getState().lastCheckResponse?.statements?.length ?? 0;

    if (processBtn) {
      processBtn.disabled = !hasDocument;
      processBtn.classList.toggle('disabled', !hasDocument);
    }
    if (contradictionsBtn) {
      const enabled = ruleCount > 0;
      contradictionsBtn.disabled = !enabled;
      contradictionsBtn.classList.toggle('disabled', !enabled);
    }
    if (contradictionsCount) {
      if (ruleCount > 0) {
        contradictionsCount.textContent = String(ruleCount);
        contradictionsCount.classList.remove('d-none');
      } else {
        contradictionsCount.classList.add('d-none');
      }
    }
  }

  /**
   * Show/hide loading state
   * @param {boolean} loading - Whether to show loading
   */
  setLoading(loading: boolean): void {
    if (this.elements.loading) {
      this.elements.loading.style.display = loading ? 'block' : 'none';
    }

    // Update status bar document state
    if (this.statusBar) {
      this.statusBar.setDocumentState(loading ? 'loading' : 'none');
    }
  }

  /**
   * Show error message
   * @param {string} message - Error message
   */
  override showError(message: string): void {
    const errorLabel = this.uiManager.t('status.error') || 'Error';
    this.toastService.showError(`${errorLabel}: ${message}`);
    // Reset UI state
    this.setLoading(false);
    // Only show dropArea if no document is currently loaded
    if (this.elements.dropArea && !this.currentDocument) {
      this.elements.dropArea.style.display = 'block';
    }
    if (this.elements.landingPage) {
      this.elements.landingPage.style.display = 'flex';
    }
  }

  /** The open document's name, or the translated "No document open". */
  private renderTopbarFilename(): void {
    const element = this.elements.topbarFilename;
    if (!element) return;
    const name = this.topbarDocumentName;
    element.textContent =
      name !== null
        ? `📄 ${name}`
        : this.uiManager.t('topbar.noDocument') || 'No document open';
    element.classList.toggle('topbar__filename--active', name !== null);
  }

  /**
   * Update UI based on current state
   */
  override updateUI(): void {
    // Initial UI state
    if (this.elements.loading) {
      this.elements.loading.style.display = 'none';
    }
    if (this.elements.documentContent) {
      this.elements.documentContent.style.display = 'none';
    }
    if (this.elements.dropArea) {
      this.elements.dropArea.style.display = 'block';
    }
    if (this.elements.landingPage) {
      this.elements.landingPage.style.display = 'flex';
    }
    this.topbarDocumentName = null;
    this.renderTopbarFilename();
    const sidebarHint = document.getElementById('sidebar-hint');
    if (sidebarHint) sidebarHint.style.display = 'block';
    // Load button is always visible, no need to show/hide
  }
}

// Initialize app when DOM is ready
let app: WebApp;

document.addEventListener('DOMContentLoaded', async () => {
  try {
    app = new WebApp();
    await app.initialize();

    // Attach event listeners for Analyse buttons (replaces inline onclick handlers)
    document
      .getElementById('check-rules-btn')
      ?.addEventListener('click', () => app.checkDocumentRules());
    document
      .getElementById('show-contradictions-btn')
      ?.addEventListener('click', () => app.showContradictionsOffcanvas());

    // Expose app globally only in development (not production)
    if (import.meta.env.DEV) {
      window.app = app;
    }
  } catch (error) {
    logger.error('Failed to initialize web app:', error);

    // Don't strand the user behind the opaque splash — offer a reload.
    const splash = new SplashScreen();
    if (splash.exists()) {
      splash.showError(() => window.location.reload());
    }

    // Use ToastService directly since app may not be initialized
    ToastService.getInstance().showError(
      `Failed to initialize application: ${(error as Error).message}`,
      { title: 'IMSight' }
    );
  }
});

export default WebApp;
